import { BooleanInput } from "@angular/cdk/coercion";
import { SelectionModel } from '@angular/cdk/collections';
import { Location } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Country } from '@app/core/models/country.model';
import { CustomerAddress } from '@app/core/models/customer-address.model';
import { CustomerContact } from "@app/core/models/customer-contact.model";
import { CustomerInternet } from '@app/core/models/customer-internet.model';
import { Customer } from "@app/core/models/customer.model";
import { CountryService } from '@app/core/services/country.service';
import { CustomerService } from "@app/core/services/customer.service";
import { UserAlertService } from "@app/shared/services/user-alert.service";
import { CassError } from '@core/models/cass-error';
import { Logger } from '@core/services/logger.service';
import { UserProfilesService } from '@core/services/user-profiles.service';
import { SubSink } from 'subsink';

export enum ContactsFilterOption {
	Sales = "ContactsFilterOption.Sales",
	Material = "ContactsFilterOption.Material",
	Proof = "ContactsFilterOption.Proof",
}

@Component({
	selector: 'app-customer-page',
	templateUrl: './customer-page.component.html',
	styleUrls: ['./customer-page.component.css']
})
export class CustomerPageComponent implements OnInit, OnDestroy {
	@Input()
	parentForm!: FormGroup;
	private _initialDetails?: Customer = undefined;
	private _readonly: BooleanInput = true;
	customer?: Customer;
	agency?: Customer;
	form!: FormGroup;
	id: FormControl;
	name: FormControl;
	organisationNumber: FormControl;
	phone: FormControl;
	fax: FormControl;
	mobile: FormControl;
	address: FormControl;
	city: FormControl;
	country: FormControl;
	zipcode: FormControl;
	province: FormControl;
	private subs = new SubSink();

	public countries: Country[] = [];

	private readonly COMPONENT_NAME = "CustomerPageComponent";

	customerContactTableColumnDefs = [
		{ key: "firstName", heading: "CUSTOMER_DETAILS.CONTACT_DETAILS.FIRST_NAME" },
		{ key: "lastName", heading: "CUSTOMER_DETAILS.CONTACT_DETAILS.LAST_NAME" },
		{ key: "email", heading: "CUSTOMER_DETAILS.CONTACT_DETAILS.EMAIL" },
		{ key: "phone", heading: "CUSTOMER_DETAILS.CONTACT_DETAILS.PHONE" },
		{ key: "fax", heading: "CUSTOMER_DETAILS.CONTACT_DETAILS.FAX" },
		{ key: "mobile", heading: "CUSTOMER_DETAILS.CONTACT_DETAILS.MOBILE" },
		{ key: "address", heading: "CUSTOMER_DETAILS.CONTACT_DETAILS.ADDRESS" },
		{ key: "city", heading: "CUSTOMER_DETAILS.CONTACT_DETAILS.CITY" },
		{ key: "province", heading: "CUSTOMER_DETAILS.CONTACT_DETAILS.PROVINCE" },
		{ key: "country", heading: "CUSTOMER_DETAILS.CONTACT_DETAILS.COUNTRY", value: (data: CustomerContact): string => this.getCountryName(data.country), class: "align-right" },
		{ key: "zipCode", heading: "CUSTOMER_DETAILS.CONTACT_DETAILS.ZIPCODE" },
		{ key: "jobRole", heading: "CUSTOMER_DETAILS.CONTACT_DETAILS.JOB_ROLE", value: (data: CustomerContact): string => this.getJobRoleLabel(data), class: "align-right" },
		{ key: "jobTitle", heading: "CUSTOMER_DETAILS.CONTACT_DETAILS.JOB_TITLE" }
	];

	customerWebsiteTableColumnDefs = [
		{ key: "address", heading: "CUSTOMER_DETAILS.WEBSITE_DETAILS.WEBSITE_URL" },
		{ key: "description", heading: "CUSTOMER_DETAILS.WEBSITE_DETAILS.DESCRIPTION", class: undefined },
	];

	customerEmailTableColumnDefs = [
		{ key: "address", heading: "CUSTOMER_DETAILS.EMAIL_DETAILS.EMAIL_ADDRESS" },
		{ key: "description", heading: "CUSTOMER_DETAILS.EMAIL_DETAILS.DESCRIPTION", class: undefined },
	];

	customerAddressTableColumnDefs = [
		{ key: "description", heading: "CUSTOMER_DETAILS.ADDRESS_DETAILS.DESCRIPTION", class: undefined },
		{ key: "address", heading: "CUSTOMER_DETAILS.ADDRESS_DETAILS.ADDRESS" },
		{ key: "city", heading: "CUSTOMER_DETAILS.ADDRESS_DETAILS.CITY" },
		{ key: "zipCode", heading: "CUSTOMER_DETAILS.ADDRESS_DETAILS.ZIPCODE" },
		{ key: "province", heading: "CUSTOMER_DETAILS.ADDRESS_DETAILS.PROVINCE" },
		{ key: "country", heading: "CUSTOMER_DETAILS.ADDRESS_DETAILS.COUNTRY", value: (data: CustomerAddress): string => this.getCountryName(data.country), class: "align-right" },
	];

	initialContactSelection = [];
	initialEmailSelection = [];
	initialWebsiteSelection = [];
	initialAddressSelection = [];
	contactSelection = new SelectionModel<CustomerContact>(true, this.initialContactSelection);
	emailSelection = new SelectionModel<CustomerInternet>(true, this.initialEmailSelection);
	websiteSelection = new SelectionModel<CustomerInternet>(true, this.initialWebsiteSelection);
	addressSelection = new SelectionModel<CustomerAddress>(true, this.initialAddressSelection);
	customerContactTableDisplayedColumns = this.customerContactTableColumnDefs.map(x => x.key);
	customerEmailTableDisplayedColumns = this.customerEmailTableColumnDefs.map(x => x.key);
	customerWebsiteTableDisplayedColumns = this.customerWebsiteTableColumnDefs.map(x => x.key);
	customerAddressTableDisplayedColumns = this.customerAddressTableColumnDefs.map(x => x.key);

	/** Whether the number of selected elements matches the total number of rows. */
	isAllSelected() {
		const numSelected = this.contactSelection.selected.length;
		const numRows = this.customer?.contacts?.length;
		return numSelected == numRows;
	}

	formatBoolean(boolValue: any): string {
		if (boolValue != undefined && boolValue == true)
			return "\u2714"; // tick
		return "";
	}

	// Hack : I shoudlnt have to do this.
	getJobRoleLabel(custContact: CustomerContact): string {
		if (custContact && custContact.jobRole && custContact.jobRole.label)
			return custContact.jobRole?.label?.valueOf();
		return "";
	}

	getCountryName(code: string | undefined): string {
		if (code === undefined) return "";
		const arr = this.countries.filter(x => x.code === code);
		if (arr && arr.length > 0 && arr[0].label && arr[0].label.length > 0) {
			return arr[0].label;
		}
		return code;
	}


	/** Selects all rows if they are not all selected; otherwise clear selection. */
	toggleAllRows() {
		//this.isAllSelected() ?
		//	this.selection.clear() :
		//	this.customer?.contacts?.forEach(row => this.selection.select(row));
	}

	isReadOnly() {
		return this._readonly;
	}

	isValid() {
		return this?.form?.status == "VALID";
	}


	addCustomerContact() {

		const preselectMaterial: boolean = this.selectedContactsFilterOption === ContactsFilterOption.Material;
		const preselectProof: boolean = this.selectedContactsFilterOption === ContactsFilterOption.Proof;

		this._router.navigate(["customerContact"], { state: { customer: this.customer, agency: this.agency, countries: this.countries, preselectMaterial: preselectMaterial, preselectProof: preselectProof } });
	}

	clickedContact(idx: CustomerContact) {
		this._router.navigate(["customerContact"], { state: { customer: this.customer, agency: this.agency, selections: this.contactSelection, countries: this.countries, contact: idx } });
	}

	/*removeCustomerContact() {
		if (this != undefined && this.customer != undefined && this.customer.contacts != undefined && this.contactSelection.selected.length > 0) {
			var modified = false;
			// any contacts we wish to remove... remove them
			for(var selectedIndex = this.contactSelection.selected.length-1; selectedIndex>=0; selectedIndex--) {
				const contact = this.contactSelection.selected[selectedIndex];
				for(var index = this.customer.contacts.length-1; index >= 0; index--) {
					if (this.customer.contacts[index].id == contact?.id) {
						//this.customer.contacts[index].id = "DELETE + "
						this.customer.contacts.splice(index,1);
						modified = true;
						break;
					}
				}
			}
			// once removed we then delete them from the customer.
			if (modified) {
					this._customerService.createOrUpdateCustomer(this?.agency?.id, this.customer).
					subscribe(
						updatedCustomer => {
							this.customer = updatedCustomer;
							//this._router.navigate(["advertiser"], { state: { customer: updatedCustomer, agency: this.agency } });
						},
						// TODO NOT SURE WHAT WE WANT TO DO FOR ERROR HANDLING
						error => { this._userAlertService.error("FAILED:${error}")});

				} else {
					this._router.navigate(["advertiser"], { state: { customer: this.customer, agency: this.agency } });
				}
			}

	} */

	addCustomerEmail() {
		this._router.navigate(["customerEmail"], { state: { customer: this.customer, agency: this.agency } });
	}

	/* modifyCustomerEmail() {
		if (this.emailSelection.selected.length > 0) {
			this._router.navigate(["customerEmail"], { state: { customer: this.customer, agency: this.agency, selections: this.emailSelection } });
		}
	} */

	clickedEmail(idx: CustomerInternet) {
		this._router.navigate(["customerEmail"], { state: { customer: this.customer, agency: this.agency, selections: this.contactSelection, countries: this.countries, internet: idx } });
	}

	/*removeCustomerEmail() {
		if (this != undefined && this.customer != undefined && this.customer.emails != undefined && this.emailSelection.selected.length > 0) {
			var modified = false;
			// any emails we wish to remove... remove them
			for(var selectedIndex = this.emailSelection.selected.length-1; selectedIndex>=0; selectedIndex--) {
				const email = this.emailSelection.selected[selectedIndex];
				for(var index = this.customer.emails.length-1; index >= 0; index--) {
					if (this.customer.emails[index].id == email?.id) {
						this.customer.emails.splice(index,1);
						modified = true;
						break;
					}
				}
			}
			// once removed we then delete them from the customer.
			if (modified) {
					this._customerService.createOrUpdateCustomer(this?.agency?.id, this.customer).
					subscribe(
						updatedCustomer => {
							this.customer = updatedCustomer;
						},
						// TODO NOT SURE WHAT WE WANT TO DO FOR ERROR HANDLING
						error => { this._userAlertService.error("FAILED")});

				} else {
					this._router.navigate(["advertiser"], { state: { customer: this.customer, agency: this.agency } });
				}
			}

	} */


	addCustomerWebsite() {
		this._router.navigate(["customerWebsite"], { state: { customer: this.customer, agency: this.agency } });
	}

	/*modifyCustomerWebsite() {
		if (this.websiteSelection.selected.length > 0) {
			this._router.navigate(["customerWebsite"], { state: { customer: this.customer, agency: this.agency, selections: this.websiteSelection } });
		}
	} */

	clickedWebsite(idx: CustomerInternet) {
		this._router.navigate(["customerWebsite"], { state: { customer: this.customer, agency: this.agency, selections: this.contactSelection, countries: this.countries, internet: idx } });
	}


	/* removeCustomerWebsite() {
		if (this != undefined && this.customer != undefined && this.customer.urls != undefined && this.websiteSelection.selected.length > 0) {
			var modified = false;
			// any emails we wish to remove... remove them
			for(var selectedIndex = this.websiteSelection.selected.length-1; selectedIndex>=0; selectedIndex--) {
				const website = this.websiteSelection.selected[selectedIndex];
				for(var index = this.customer.urls.length-1; index >= 0; index--) {
					if (this.customer.urls[index].id == website?.id) {
						this.customer.urls.splice(index,1);
						modified = true;
						break;
					}
				}
			}
			// once removed we then delete them from the customer.
			if (modified) {
					this._customerService.createOrUpdateCustomer(this?.agency?.id, this.customer).
					subscribe(
						updatedCustomer => {
							this.customer = updatedCustomer;
						},
						// TODO NOT SURE WHAT WE WANT TO DO FOR ERROR HANDLING
						error => { this._userAlertService.error("FAILED")});

				} else {
					this._router.navigate(["advertiser"], { state: { customer: this.customer, agency: this.agency } });
				}
			}

	} */

	addCustomerAddress() {
		this._router.navigate(["customerAddress"], { state: { customer: this.customer, agency: this.agency, countries: this.countries } });
	}

	/*modifyCustomerAddress() {
		if (this.addressSelection.selected.length > 0) {
			this._router.navigate(["customerAddress"], { state: { customer: this.customer, agency: this.agency, selections: this.addressSelection, countries: this.countries } });
		}
	} */

	clickedAddress(idx: CustomerAddress) {
		this._router.navigate(["customerAddress"], { state: { customer: this.customer, agency: this.agency, selections: this.contactSelection, countries: this.countries, address: idx } });
	}

	/* removeCustomerAddress() {
		if (this != undefined && this.customer != undefined && this.customer.addresses != undefined && this.addressSelection.selected.length > 0) {
			var modified = false;
			// any items we wish to remove... remove them
			for(var selectedIndex = this.addressSelection.selected.length-1; selectedIndex>=0; selectedIndex--) {
				const address = this.addressSelection.selected[selectedIndex];
				for(var index = this.customer.addresses.length-1; index >= 0; index--) {
					if (this.customer.addresses[index].id == address?.id) {
						this.customer.addresses.splice(index,1);
						modified = true;
						break;
					}
				}
			}
			// once removed we then delete them from the customer.
			if (modified) {
					this._customerService.createOrUpdateCustomer(this?.agency?.id, this.customer).
					subscribe(
						updatedCustomer => {
							this.customer = updatedCustomer;
						},
						// TODO NOT SURE WHAT WE WANT TO DO FOR ERROR HANDLING
						error => { this._userAlertService.error("FAILED")});

				} else {
					this._router.navigate(["advertiser"], { state: { customer: this.customer, agency: this.agency } });
				}
			}

	} */

	set Country(code: string) {
		this.setFormValueIfNotEmpty("country", code);
	}
	get Country() {
		return this.getFormValueIfNotEmpty("country");
	}

	initDefaults() {
		// return any values back to initial states
		if (this.form && this != undefined && this.customer != undefined) {
			this.setFormValueIfNotEmpty("id", this.customer.id);
			this.setFormValueIfNotEmpty("name", this.customer.name);
			this.setFormValueIfNotEmpty("organisationNumber", this.customer.organisationNumber);
			this.setFormValueIfNotEmpty("phone", this.customer.phone);
			this.setFormValueIfNotEmpty("fax", this.customer.fax);
			this.setFormValueIfNotEmpty("mobile", this.customer.mobile);
			this.setFormValueIfNotEmpty("address", this.customer.address);
			this.setFormValueIfNotEmpty("city", this.customer.city);
			this.setFormValueIfNotEmpty("country", this.customer.country);
			this.setFormValueIfNotEmpty("zipcode", this.customer.zipCode);
			this.setFormValueIfNotEmpty("province", this.customer.province);
		}
	}

	clickEditButton() {
		this._readonly = !this._readonly;
		if (this._readonly) {
			this.initDefaults();
		}
	}

	public ContactsFilterOption = ContactsFilterOption; // allow this type to be used by HTML
	public filteredCustomerContacts: CustomerContact[] = [];

	public selectedContactsFilterOption: ContactsFilterOption = ContactsFilterOption.Sales;

	constructor (private formBuilder: FormBuilder,
		private _route: ActivatedRoute,
		private _router: Router,
		private _userAlertService: UserAlertService,
		private _customerService: CustomerService,
		private _countriesService: CountryService,
		private _location: Location,
		private _userProfilesService: UserProfilesService,
		private _logger: Logger
	) {
		this.id = this.formBuilder.control(this._initialDetails?.id || "");
		this.name = this.formBuilder.control(this._initialDetails?.name || "", [Validators.required, Validators.maxLength(40)]);
		this.organisationNumber = this.formBuilder.control(this._initialDetails?.organisationNumber || "", Validators.maxLength(15));
		this.phone = this.formBuilder.control(this._initialDetails?.phone || "", [Validators.required, Validators.maxLength(16)]);
		this.fax = this.formBuilder.control(this._initialDetails?.fax || "", [Validators.maxLength(16)]);
		this.mobile = this.formBuilder.control(this._initialDetails?.mobile || "", [Validators.maxLength(16)]);
		this.address = this.formBuilder.control(this._initialDetails?.address || "", [Validators.required, Validators.maxLength(40)]);
		this.city = this.formBuilder.control(this._initialDetails?.city || "", [Validators.required, Validators.maxLength(40)]);
		this.country = this.formBuilder.control(this._initialDetails?.country || "", [Validators.maxLength(2)]);
		this.zipcode = this.formBuilder.control(this._initialDetails?.zipCode || "", [Validators.required, Validators.maxLength(12)]);
		this.province = this.formBuilder.control(this._initialDetails?.province || "", [Validators.maxLength(40)]);
		const nav = this._router.getCurrentNavigation();
		if (nav && nav.extras && nav.extras.state) {
			this.customer = nav.extras.state['customer'];
			this.agency = nav.extras.state['agency'];
		}
		this.form = new FormGroup({});
	}

	ngOnInit(): void {
		this.form.addControl("id", this.id);
		this.form.addControl("name", this.name);
		this.form.addControl("organisationNumber", this.organisationNumber);
		this.form.addControl("phone", this.phone);
		this.form.addControl("fax", this.fax);
		this.form.addControl("mobile", this.mobile);
		this.form.addControl("address", this.address);
		this.form.addControl("city", this.city);
		this.form.addControl("country", this.country);
		this.form.addControl("zipcode", this.zipcode);
		this.form.addControl("province", this.province);

		if (this.customer) {
			this._initialDetails = this.customer;
			this.initDefaults();

			if (this.customer.contacts && this.customer.contacts.length > 0) {
				this.filterCustomerContacts();
			}
		}

		this._countriesService.getCountries().subscribe({
			next: (ok) => {
				this.countries = ok;
			},
			error: (cassError: CassError) => {
				this._userAlertService.errorByCassError(cassError);
			}
		});

		if (this.customer && this.customer.id) {
			// notify the profile service that the advertiser was accessed
			this._userProfilesService.notifyCustomerAccessed(this.customer.id);
		}

		if (this.agency == null) {
			this.subs.add(
				this._userProfilesService.userProfile$.subscribe({
					next: (profile) => {
						if (profile?.customers?.[0] != undefined) {
							this.agency = profile.customers[0];
						};
					},
					error: (error: CassError) => {
						this._logger.error(this.COMPONENT_NAME, "Init failed: cannot get user profile or agency's customers");
						//this._alertService.errorByCassError(error);
					}
				}));
		}

	}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
	}

	private setFormValueIfNotEmpty(fieldName: string, fieldValue?: string) {
		if (this.form && fieldValue) {
			this.form.controls[fieldName].setValue(fieldValue);
		}
	}
	private getFormValueIfNotEmpty(fieldName: string) {
		return this.form?.controls[fieldName]?.value;
	}

	modifyCustomer() {
		if (this != undefined && this.customer != undefined) {
			this.customer.name = this.getFormValueIfNotEmpty("name");
			this.customer.organisationNumber = this.getFormValueIfNotEmpty("organisationNumber");
			this.customer.phone = this.getFormValueIfNotEmpty("phone");
			this.customer.fax = this.getFormValueIfNotEmpty("fax");
			this.customer.mobile = this.getFormValueIfNotEmpty("mobile");
			this.customer.address = this.getFormValueIfNotEmpty("address");
			this.customer.city = this.getFormValueIfNotEmpty("city");
			this.customer.country = this.getFormValueIfNotEmpty("country");
			this.customer.zipCode = this.getFormValueIfNotEmpty("zipcode");
			this.customer.province = this.getFormValueIfNotEmpty("province");

			this._customerService.createOrUpdateCustomer(this?.agency?.id, this.customer).subscribe({
				next: (ok) => {
					this.customer = ok;
					this.clickEditButton();
					this._location.go("/advertiser", "", { customer: this.customer, agency: this.agency });
				},
				error: (cassError: CassError) => {
					this._userAlertService.errorByCassError(cassError);
				}
			});
		}
	}

	public onContactsFilterOptionChange(option: ContactsFilterOption) {
		this.selectedContactsFilterOption = option;
		this.filterCustomerContacts();
	}

	private filterCustomerContacts() {
		switch (this.selectedContactsFilterOption) {
			case ContactsFilterOption.Sales:
				this.filteredCustomerContacts = this.customer?.contacts?.filter((contact: CustomerContact) => contact.default || (!contact.proofContact && !contact.materialContact)) || [];
				break;
			case ContactsFilterOption.Material:
				this.filteredCustomerContacts = this.customer?.contacts?.filter((contact: CustomerContact) => contact.materialContact) || [];
				break;
			case ContactsFilterOption.Proof:
				this.filteredCustomerContacts = this.customer?.contacts?.filter((contact: CustomerContact) => contact.proofContact) || [];
				break;
			default:
				this._logger.error(this.COMPONENT_NAME, `The value of selectedContactsFilterOption="${this.selectedContactsFilterOption}" is not supported. Possible bug.`);
				break;
		}
	}
}
